import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Panel, TactLoader } from "../components/Tactical";
import { useT } from "../context/LanguageContext";
import { toast } from "sonner";
import { Shield, Send, UserMinus, RefreshCw, MessageCircle as ChatIcon, Crown } from "lucide-react";
import WhatsAppButton from "../components/WhatsAppButton";
import DMChatDrawer from "../components/DMChatDrawer";

/**
 * Admin "Moderators" tab — lists every user with role="moderator" the admin
 * has promoted, with quick actions:
 *   • DM (opens the inline chat drawer used on the Social page)
 *   • WhatsApp (deep-links via wa.me)
 *   • Demote (sets role back to "player")
 *
 * Pulls from the existing GET /api/admin/users endpoint and filters
 * client-side so we don't have to add a new backend route.
 */
export default function ModeratorsTab() {
  const { t } = useT();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState({});
  const [chatPeer, setChatPeer] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/users");
      const mods = (Array.isArray(data) ? data : [])
        .filter((u) => u.role === "moderator")
        .sort((a, b) => (a.username || "").localeCompare(b.username || ""));
      setRows(mods);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  };

  useEffect(() => { refresh(); }, []);

  const demote = async (u) => {
    if (!window.confirm(t("mods.demoteConfirm", { name: u.username }))) return;
    setBusy((b) => ({ ...b, [u.id]: true }));
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: "player" });
      toast.success(t("mods.demoteToast", { name: u.username }));
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [u.id]: false }));
    }
  };

  // Senior Moderator is a flag on top of role="moderator" (keeps all mod perms).
  const setSenior = async (u, senior) => {
    if (senior && !window.confirm(`Promote ${u.username} to Senior Moderator?`)) return;
    if (!senior && !window.confirm(`Revert ${u.username} to a standard moderator?`)) return;
    setBusy((b) => ({ ...b, [u.id]: true }));
    try {
      await api.patch(`/admin/users/${u.id}/senior`, { senior });
      toast.success(senior
        ? `${u.username} is now a Senior Moderator`
        : `${u.username} reverted to moderator`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [u.id]: false }));
    }
  };

  if (rows === null) return <TactLoader label={t("mods.loading")} />;

  return (
    <div className="space-y-5" data-testid="moderators-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-1">{t("mods.kicker")}</div>
          <h2 className="font-heading text-3xl uppercase tracking-wider">{t("mods.title")}</h2>
          <p className="text-sm text-[#71717A] font-body max-w-xl">{t("mods.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={refresh}
          data-testid="mods-refresh"
          className="inline-flex items-center gap-2 border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors"
        >
          <RefreshCw className="w-3 h-3" /> {t("mods.refresh")}
        </button>
      </div>

      {rows.length === 0 ? (
        <Panel testId="mods-empty" className="font-mono text-sm text-[#71717A]">
          {t("mods.empty")}
        </Panel>
      ) : (
        <Panel testId="mods-list" className="overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="mods-table">
            <thead className="bg-[#F4F4F5] dark:bg-[#0E0E14]">
              <tr className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
                <th className="text-start px-4 py-3">{t("mods.col.player")}</th>
                <th className="text-start px-4 py-3">{t("mods.col.contact")}</th>
                <th className="text-start px-4 py-3">{t("mods.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u, i) => (
                <tr
                  key={u.id}
                  data-testid={`mods-row-${u.id}`}
                  className={`border-t border-[#E4E4E7] dark:border-[#2A2A30] font-body ${i % 2 === 1 ? "bg-[#FAFAFC]/40 dark:bg-[#0A0A0F]/40" : ""}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover border border-[#7C3AED]/40 flex-shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/40 flex items-center justify-center flex-shrink-0">
                          <Shield className="w-4 h-4 text-[#7C3AED]" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-bold text-[#09090B] dark:text-[#F4F4F5] truncate flex items-center gap-1.5" data-testid={`mods-name-${u.id}`}>
                          {u.username}
                          {u.senior_moderator && (
                            <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-[#FFD700]/15 text-[#B8860B] dark:text-[#FFD700] border border-[#FFD700]/40" data-testid={`mods-senior-badge-${u.id}`}>
                              <Crown className="w-2.5 h-2.5" /> Senior
                            </span>
                          )}
                        </div>
                        {u.club_name && (
                          <div className="font-mono text-[10px] text-[#7C3AED] uppercase tracking-[0.2em] truncate">
                            {u.club_name}
                          </div>
                        )}
                        {u.game_id && (
                          <div className="font-mono text-[10px] text-[#71717A] truncate">{u.game_id}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#71717A]">
                    {u.whatsapp || <span className="text-[#A1A1AA]">—</span>}
                    {u.email && <div className="text-[10px] mt-0.5">{u.email}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setChatPeer({ id: u.id, username: u.username, game_id: u.game_id, whatsapp: u.whatsapp, avatar_url: u.avatar_url })}
                        data-testid={`mods-dm-${u.id}`}
                        className="inline-flex items-center gap-1.5 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 transition-colors"
                      >
                        <ChatIcon className="w-3 h-3" /> {t("mods.dm")}
                      </button>
                      {u.whatsapp && (
                        <WhatsAppButton number={u.whatsapp} testId={`mods-whatsapp-${u.id}`} size="sm" />
                      )}
                      <button
                        type="button"
                        onClick={() => setSenior(u, !u.senior_moderator)}
                        disabled={busy[u.id]}
                        data-testid={`mods-senior-${u.id}`}
                        className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 transition-colors disabled:opacity-40 border ${
                          u.senior_moderator
                            ? "border-[#A1A1AA] text-[#A1A1AA] hover:bg-[#A1A1AA] hover:text-white"
                            : "border-[#FFD700] text-[#B8860B] dark:text-[#FFD700] hover:bg-[#FFD700] hover:text-[#020617]"
                        }`}
                      >
                        <Crown className="w-3 h-3" />
                        {busy[u.id]
                          ? "…"
                          : (u.senior_moderator ? "Revert to Mod" : "Promote to Senior")}
                      </button>
                      <button
                        type="button"
                        onClick={() => demote(u)}
                        disabled={busy[u.id]}
                        data-testid={`mods-demote-${u.id}`}
                        className="inline-flex items-center gap-1.5 border border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-white font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 transition-colors disabled:opacity-40"
                      >
                        <UserMinus className="w-3 h-3" /> {busy[u.id] ? t("mods.demoting") : t("mods.demote")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {chatPeer && (
        <DMChatDrawer peer={chatPeer} onClose={() => setChatPeer(null)} />
      )}
    </div>
  );
}
